/**
 * Moving a branch to the opposite side.
 *
 * The specification is unusually specific about this operation, and for good reason — it is the
 * one every mind-map tool gets wrong. Flipping a branch must preserve its id, its children, the
 * hierarchy, the sibling order, the text, the attachments, the notes, the styles, the
 * connections and the presentation steps; it must recompute only the branch involved; and it
 * must undo in a single action.
 *
 * The way to guarantee all of that is to make the operation incapable of doing anything else.
 * `planSideChange` returns nothing but a list of `side` field changes. It cannot reorder, cannot
 * reparent, cannot touch a node outside the branch, because it does not return the vocabulary to
 * do so. The layout then reruns for that subtree; positions are output, not input.
 */
import { isTwoSided } from '@orbit-mind/shared-types';
import { subtree } from './tree';
/** The side a node ends up on when flipped. `auto` has no opposite to flip to. */
export function oppositeSide(side) {
    switch (side) {
        case 'left':
            return 'right';
        case 'right':
            return 'left';
        case 'up':
            return 'down';
        case 'down':
            return 'up';
        case 'auto':
            return 'auto';
    }
}
/** The two sides a layout actually offers, or `null` when it is not two-sided. */
export function sidesFor(layoutId) {
    if (!isTwoSided(layoutId))
        return null;
    return layoutId.endsWith('/up-down') ? ['up', 'down'] : ['left', 'right'];
}
/**
 * Can this node's side be changed?
 *
 * Two conditions. The layout must place nodes on both sides — offering the action in a
 * right-only tree would be a control that does nothing. And the node must be a direct child of
 * the root, because that is where a branch chooses a side; everything deeper inherits it, and a
 * deep node with a side of its own would contradict its ancestor.
 */
export function canChangeSide(doc, nodeId) {
    if (sidesFor(doc.map.layoutId) === null)
        return false;
    const node = doc.nodes[nodeId];
    if (node === undefined)
        return false;
    return node.parentId === doc.map.rootNodeId;
}
/**
 * The changes that move `branchId` to `targetSide`.
 *
 * Applies to the whole subtree: a branch is a unit, and leaving descendants on the old side
 * would split it across the map. Nodes already on the target side are omitted, so a no-op
 * produces an empty plan and the command layer can skip creating an undo entry for nothing.
 *
 * Returns an empty plan when the change is not allowed, rather than throwing — this is called
 * from a drag gesture to decide whether to show a drop target, and an exception is not an answer
 * to "may I?".
 */
export function planSideChange(doc, branchId, targetSide) {
    if (!canChangeSide(doc, branchId))
        return [];
    const sides = sidesFor(doc.map.layoutId);
    if (sides?.includes(targetSide) !== true)
        return [];
    const changes = [];
    for (const node of subtree(doc, branchId)) {
        if (node.side === targetSide)
            continue;
        changes.push({ nodeId: node.id, from: node.side, to: targetSide });
    }
    return changes;
}
/** The changes that flip `branchId` to whichever side it is not currently on. */
export function planSideFlip(doc, branchId) {
    const node = doc.nodes[branchId];
    if (node === undefined)
        return [];
    const sides = sidesFor(doc.map.layoutId);
    if (sides === null)
        return [];
    // A branch sitting on `auto` has no current side to flip; send it to the first concrete one.
    const target = node.side === sides[0] ? sides[1] : sides[0];
    return planSideChange(doc, branchId, target);
}
/**
 * Applies a plan, returning a new document.
 *
 * Only `side` and `updatedAt` change, and only on nodes named in the plan. Everything else on
 * every node — including the node objects for untouched branches — is carried across by
 * reference, which is what makes "only the branch involved was recomputed" checkable by identity
 * rather than by inspection.
 */
export function applySideChanges(doc, changes, updatedAt, updatedBy) {
    if (changes.length === 0)
        return doc;
    const nodes = { ...doc.nodes };
    for (const change of changes) {
        const node = nodes[change.nodeId];
        if (node === undefined)
            continue;
        nodes[change.nodeId] = {
            ...node,
            side: change.to,
            updatedAt: updatedAt,
            updatedBy: updatedBy,
        };
    }
    return { ...doc, nodes };
}
/** Reverses a plan, for undo. */
export function invertSideChanges(changes) {
    return changes.map((change) => ({
        nodeId: change.nodeId,
        from: change.to,
        to: change.from,
    }));
}
//# sourceMappingURL=side.js.map